import { Divider, Typography } from '@mui/material'
import { Box } from '@mui/system'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '../../packages/utils/numberFormatters'
import { FactsTable, FactsTableRow } from '../components/FactsTable'
import { Link } from 'react-router-dom'
import { IssueEntity } from '../../types/IssueEntity'

interface OriginIssue {
    numberOfWarrants: number | null
    issueId: string
}

interface Warrant {
    id: string
    instrumentName: string
    name: string
    origins: OriginIssue[]
}

interface CompensationGuarantorsProps {
    issue: {
        linkedIssues: {
            issues: Pick<IssueEntity, 'id' | 'type' | 'unitSubTotal'>[]
        }[]
        warrants: Warrant[]
    }
}

export const CompensationGuarantors = ({ issue }: CompensationGuarantorsProps) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const linkedIssues = issue.linkedIssues
    const warrants = issue.warrants

    const getFirstGuarantorFeeIssue = (linkedIssues: CompensationGuarantorsProps['issue']['linkedIssues']) => {
        for (const group of linkedIssues) {
            if (Array.isArray(group.issues)) {
                const issue = group.issues.find((issue) => issue.type === 'Guarantor fee issue')
                if (issue) {
                    return issue // Return the first match found
                }
            }
        }
        return null // No matching issue found
    }

    const firstGuarantorFeeIssue = getFirstGuarantorFeeIssue(linkedIssues)
    if (!firstGuarantorFeeIssue) {
        // this component is only relevant if there is a guarantor fee issue
        return null
    }

    const rows: { key: string; label: string | React.ReactNode }[] = [
        {
            key: 'units-count',
            label: formatNumber(firstGuarantorFeeIssue.unitSubTotal ?? 0, { locale, decimals: 0 }) + ' aktier',
        },
        ...warrants
            .map((warrant) => ({
                originIssue: warrant.origins.find((origin) => origin.issueId === firstGuarantorFeeIssue.id),
                warrant,
            }))
            .filter(({ originIssue }) => !!originIssue)
            .map(({ originIssue, warrant }) => ({
                key: warrant.id,
                label: (
                    <>
                        <span>
                            {formatNumber(originIssue?.numberOfWarrants ?? 0, {
                                locale,
                                decimals: 0,
                            })}{' '}
                        </span>
                        <Link to={`/warrants/${warrant.id}`}>{warrant.instrumentName || warrant.name}</Link>
                    </>
                ),
            })),
    ]

    return (
        <Box
            sx={{
                mb: 4,
            }}
        >
            <Typography variant='subtitle2'>Ersättning till garantiåtagare</Typography>
            <FactsTable>
                {rows.map(({ key, label }, index) => (
                    <FactsTableRow
                        key={key}
                        label={label}
                        hasDivider={false}
                        hasBottomPadding={index === rows.length - 1}
                    />
                ))}
            </FactsTable>
            <Divider />
        </Box>
    )
}
